import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FiUserPlus,
    FiArrowLeft,
} from "react-icons/fi";
import {
    PageHeader,
    useToast,
} from "../../theme/components";
import { PageButton } from "../../theme/components/PageButton";
import { RegIdChip } from "../../theme/components/RegIdChip";
import { adminApi } from "../../api/endpoints/admin";
import { parseApiError } from "../../api/errors";
import "./AdminCreateUser.css";

const PASSWORD_MIN = 8;

const AdminCreateUser = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [form, setForm] = useState({
        email: "",
        fullName: "",
        role: "user",
        password: "",
        confirm: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [created, setCreated] = useState(null); // UserOut on success

    const update = (field) => (e) =>
        setForm((p) => ({ ...p, [field]: e.target.value }));

    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            const email = form.email.trim().toLowerCase();
            const fullName = form.fullName.trim();
            const password = form.password;
            let err = null;
            if (!email) err = "Email is required.";
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                err = "Enter a valid email address.";
            } else if (!fullName) err = "Full name is required.";
            else if (password.length < PASSWORD_MIN) {
                err = `Password must be at least ${PASSWORD_MIN} characters.`;
            } else if (password !== form.confirm) {
                err = "Passwords do not match.";
            }
            if (err) {
                toast.error(err);
                return;
            }
            setSubmitting(true);
            try {
                const user = await adminApi.createUser({
                    email,
                    fullName,
                    role: form.role,
                    password,
                });
                setCreated(user);
                toast.success(`Created ${user.email} (${user.userIdCode}).`);
            } catch (err) {
                toast.error(parseApiError(err, "Failed to create user."));
            } finally {
                setSubmitting(false);
            }
        },
        [form, toast],
    );

    if (created) {
        return (
            <div className="admin-create-user-wrapper">
                <PageHeader
                    title="User created"
                    subtitle={`${created.email} is now active.`}
                    icon={FiUserPlus}
                    actions={
                        <PageButton
                            type="button"
                            variant="ghost"
                            startIcon={<FiArrowLeft size={16} />}
                            onClick={() => navigate("/admin/users")}
                        >
                            Back to Users
                        </PageButton>
                    }
                />
                <div className="admin-create-user-success">
                    <dl>
                        <dt>User ID</dt>
                        <dd>
                            <RegIdChip value={created.userIdCode} label="PHM" />
                        </dd>
                        <dt>Email</dt>
                        <dd>{created.email}</dd>
                        <dt>Full name</dt>
                        <dd>{created.fullName || "—"}</dd>
                        <dt>Role</dt>
                        <dd>{created.role}</dd>
                    </dl>
                    <div className="admin-create-user-success__actions">
                        <PageButton
                            type="button"
                            variant="primary"
                            onClick={() => {
                                setCreated(null);
                                setForm({
                                    email: "",
                                    fullName: "",
                                    role: "user",
                                    password: "",
                                    confirm: "",
                                });
                            }}
                        >
                            Add another
                        </PageButton>
                        <PageButton
                            type="button"
                            variant="ghost"
                            onClick={() => navigate("/admin/users")}
                        >
                            Back to Users
                        </PageButton>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-create-user-wrapper">
            <PageHeader
                title="Add user"
                subtitle="Provision a new Pediatrack account. A unique PHM ID is assigned automatically."
                icon={FiUserPlus}
                actions={
                    <PageButton
                        type="button"
                        variant="ghost"
                        startIcon={<FiArrowLeft size={16} />}
                        onClick={() => navigate("/admin/users")}
                    >
                        Back to Users
                    </PageButton>
                }
            />

            <form className="admin-create-user-form" onSubmit={handleSubmit}>
                <label className="admin-create-user-field">
                    <span>Email</span>
                    <input
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="name@clinic.com"
                        autoComplete="email"
                        required
                    />
                </label>
                <label className="admin-create-user-field">
                    <span>Full name</span>
                    <input
                        type="text"
                        value={form.fullName}
                        onChange={update("fullName")}
                        placeholder="e.g. Dhanu Perera"
                        autoComplete="name"
                        required
                    />
                </label>
                <div className="admin-create-user-field">
                    <span>Role</span>
                    <div className="admin-create-user-role">
                        <label
                            className={`admin-create-user-role__opt ${
                                form.role === "user" ? "is-active" : ""
                            }`}
                        >
                            <input
                                type="radio"
                                name="role"
                                value="user"
                                checked={form.role === "user"}
                                onChange={update("role")}
                            />
                            <strong>User</strong>
                            <small>Sees only the children they register.</small>
                        </label>
                        <label
                            className={`admin-create-user-role__opt ${
                                form.role === "admin" ? "is-active" : ""
                            }`}
                        >
                            <input
                                type="radio"
                                name="role"
                                value="admin"
                                checked={form.role === "admin"}
                                onChange={update("role")}
                            />
                            <strong>Admin</strong>
                            <small>Sees every profile + manages users.</small>
                        </label>
                    </div>
                </div>
                <label className="admin-create-user-field">
                    <span>
                        Password{" "}
                        <small>(min {PASSWORD_MIN} characters)</small>
                    </span>
                    <input
                        type="password"
                        value={form.password}
                        onChange={update("password")}
                        autoComplete="new-password"
                        minLength={PASSWORD_MIN}
                        required
                    />
                </label>
                <label className="admin-create-user-field">
                    <span>Confirm password</span>
                    <input
                        type="password"
                        value={form.confirm}
                        onChange={update("confirm")}
                        autoComplete="new-password"
                        minLength={PASSWORD_MIN}
                        required
                    />
                </label>

                <div className="admin-create-user-actions">
                    <PageButton
                        type="button"
                        variant="ghost"
                        onClick={() => navigate("/admin/users")}
                        disabled={submitting}
                    >
                        Cancel
                    </PageButton>
                    <PageButton
                        type="submit"
                        variant="primary"
                        disabled={submitting}
                        startIcon={<FiUserPlus size={16} />}
                    >
                        {submitting ? "Creating…" : "Create user"}
                    </PageButton>
                </div>
            </form>
        </div>
    );
};

export default AdminCreateUser;
